import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import MarketingTextSection from "../MarketingText/MarketingText";
import DestinationSelect from "../common/DestinationSelect";
import { useToast } from "../../../context/ToastContext";
import GeoImageUpload from "../GeoImageUpload/GeoImageUpload";
import styles from "./AddEditZonesModal.module.css";

const emptyForm = {
  name: "",
  code: "",
  destinationId: "",
  active: true,
  mainImage: "",
  marketing: {
    title: "",
    para1: "",
    para2: ""
  }
};

const AddEditZonesModal = ({
  open,
  onClose,
  mode = "add",
  initialData,
  onSave,
  saveLoading = false
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);

    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        code: initialData.zoneCode ?? initialData.code ?? "",
        destinationId: initialData.destinationId ?? "",
        active: initialData.active ?? true,
        mainImage: initialData.mainImage || "",
        marketing: {
          title: initialData.marketing?.title ?? initialData.title ?? "",
          para1: initialData.marketing?.para1 ?? initialData.paragraph1 ?? "",
          para2: initialData.marketing?.para2 ?? initialData.paragraph2 ?? ""
        }
      });
    }

    if (mode === "add") {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleMarketingChange = marketing => {
    setForm(prev => ({ ...prev, marketing }));
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      showToast("Zone name is required", "error");
      return;
    }
    if (!form.code?.trim()) {
      showToast("Zone code is required", "error");
      return;
    }
    if (!form.destinationId) {
      showToast("City (destination) is required", "error");
      return;
    }
    setSubmitError(null);
    const payload = {
      zoneCode: form.code.trim().toUpperCase(),
      destinationId: Number(form.destinationId),
      active: form.active,
      mainImage: form.mainImage || null,
      name: form.name.trim(),
      title: (form.marketing?.title || "").trim(),
      paragraph1: (form.marketing?.para1 || "").trim(),
      paragraph2: (form.marketing?.para2 || "").trim(),
      description: (form.marketing?.para1 || "").trim()
    };
    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      setSubmitError(err.message || "Failed to save zone");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "add" ? "Add Zone" : "Edit Zone"}
      width="680px"
      footer={
        <>
          {submitError && (
            <span className={styles.submitError}>{submitError}</span>
          )}
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : mode === "add" ? (
              "+ Create Zone"
            ) : (
              "Save Changes"
            )}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label>Zone Name</label>
          <input
            value={form.name}
            onChange={e => handleChange("name", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Zone Code</label>
          <input
            value={form.code}
            onChange={e =>
              handleChange("code", e.target.value.toUpperCase())
            }
          />
        </div>

        <div className={styles.fieldWide}>
          <label>City / Destination</label>
          {/* Type-to-search: the cities list is far too large for a fixed dropdown, so it's
              queried from the API as you type. */}
          <DestinationSelect
            placeholder="Select city (destination)"
            value={form.destinationId ? String(form.destinationId) : ""}
            width="100%"
            onChange={e => handleChange("destinationId", e.target.value)}
          />
        </div>

        <div className={styles.statusRow}>
          <label>{form.active ? "Active Zone" : "Inactive Zone"}</label>
          <ToggleSwitch
            checked={form.active}
            onChange={() =>
              handleChange("active", !form.active)
            }
          />
        </div>
      </div>

      <div className={styles.field}>
        <label>Main Image</label>
        <GeoImageUpload value={form.mainImage} onChange={(url) => handleChange("mainImage", url)} />
      </div>

      <MarketingTextSection
        value={form.marketing}
        onChange={handleMarketingChange}
      />
    </BaseModal>
  );
};

export default AddEditZonesModal;
